import { Segmented } from './Segmented'
import { Switch } from './Switch'
import { Button } from './Button'
import { updateSettings } from '@/data/user'
import { useOther } from '@/hooks/usePaths'
import { canConnectGoogle } from '@/lib/google'
import { isShown, ownerOf } from '@/logic/events'
import { useAuth } from '@/store/auth'
import { connectGoogle, disconnectGoogle, useCalendar } from '@/store/calendar'
import { useHousehold } from '@/store/household'
import { useUi } from '@/store/ui'
import type { CalendarOwner } from '@/types'
import './SourcePanel.css'

interface SourcePanelProps {
  /** In Settings the panel also offers Disconnect. */
  full?: boolean
}

/**
 * The calendar source list: each of the person's calendars with a toggle and
 * whose it is, and the connect, reconnect or disconnect action. Cuplzap only
 * reads these calendars. Whose a calendar is decides the colour its events take
 * and which of the Whose filters shows them.
 */
export function SourcePanel({ full = false }: SourcePanelProps) {
  const user = useAuth((s) => s.user)!
  const settings = useHousehold((s) => s.userDoc?.settings)
  const other = useOther()
  const otherEmail = useHousehold((s) => s.household?.memberEmails.find((e) => e !== user.email) ?? null)
  const online = useUi((s) => s.online)
  const connection = useCalendar((s) => s.connection)
  const busy = useCalendar((s) => s.busy)
  const error = useCalendar((s) => s.error)
  const calendars = useCalendar((s) => s.calendars)
  const asOf = useCalendar((s) => s.asOf)
  const fromCache = useCalendar((s) => s.fromCache)

  const connect = () => void connectGoogle(user.email, settings).catch(() => undefined)

  const toggle = (id: string, on: boolean) => void updateSettings(user.uid, { shownCalendars: { ...(settings?.shownCalendars ?? {}), [id]: on } })
  const setOwner = (id: string, owner: CalendarOwner) => void updateSettings(user.uid, { calendarOwners: { ...(settings?.calendarOwners ?? {}), [id]: owner } })

  if (connection === 'unknown' || connection === 'checking') {
    return <p className="src__line">Checking Google Calendar…</p>
  }

  if (connection !== 'connected') {
    return (
      <div className="src">
        <p className="src__line">
          {connection === 'revoked' ? 'Google Calendar access has lapsed. Reconnect to see events again.' : 'See your Google Calendar events beside your tasks. Cuplzap only reads them; it never adds or changes anything in Google.'}
        </p>
        <Button variant="primary" onClick={connect} disabled={busy || !online || !canConnectGoogle}>
          {connection === 'revoked' ? 'Reconnect' : 'Connect Google Calendar'}
        </Button>
        {!online && <p className="src__hint">Needs a connection.</p>}
        {!canConnectGoogle && <p className="src__hint">Not set up yet: the OAuth client id is missing from the app config.</p>}
        {error && <p className="src__error">{error}</p>}
      </div>
    )
  }

  return (
    <div className="src">
      <ul className="src__list" aria-label="Calendars">
        {calendars.map((c) => {
          const on = isShown(c, settings)
          const owner = ownerOf(c, settings, otherEmail)
          return (
            <li key={c.id} className="src__row">
              <button type="button" className="src__toggle" aria-pressed={on} aria-label={`Show ${c.name} calendar`} onClick={() => toggle(c.id, !on)}>
                <span className="src__dot" style={{ background: c.color }} aria-hidden="true" />
                <span className="src__name">{c.name}</span>
                <Switch on={on} />
              </button>
              <div className="src__owner">
                <Segmented<CalendarOwner>
                  label={`Whose is ${c.name}`}
                  value={owner}
                  options={[
                    { value: 'me', label: 'Mine' },
                    { value: 'other', label: other?.name ?? 'Theirs' },
                    { value: 'both', label: 'Both' },
                  ]}
                  onChange={(v) => setOwner(c.id, v)}
                />
              </div>
            </li>
          )
        })}
      </ul>
      {fromCache && asOf && <p className="src__hint">Showing events as of {new Date(asOf).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.</p>}
      {error && <p className="src__error">{error}</p>}
      {full && (
        <>
          <p className="src__hint">Each of you sees the calendars your own Google account can see. To see each other's events, share your calendars with each other in Google Calendar.</p>
          <Button variant="secondary" onClick={() => void disconnectGoogle()} disabled={busy || !online}>
            Disconnect Google Calendar
          </Button>
        </>
      )}
    </div>
  )
}
